import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { storeTokens } from "@/api/auth-tokens";
import { NV_PATHS, NvAlertDialog, NvButton, NvConsentPromptDialog, NvDropdownField, NvFormField, NvRequiredLabel, NvTermsModal } from "@/components/navigator";
import { SpinnerOverlay } from "@/components/ui/spinner-overlay";
import { clearInviteIdentity, readInviteIdentity, storeInviteIdentity, useSsoLogin } from "@/hooks/use-sso-login";
import { getApiError, getApiFieldErrors, getApiStatus } from "@/lib/api-error";
import {
  REQUIRED_SIGNUP_TERMS,
  SIGNUP_TERMS,
  completeInviteSignup,
  lookupInvite,
  type InviteLookup,
} from "@/services/navigator/signup-service";

import {
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY,
  LoginHint,
  NoticeScreen,
  OPTIONAL_PROMPT,
  OPTIONAL_TERMS_CODE,
  SIGNUP_TERMS_TAB_LABELS,
  SectionTitle,
  SignupHeading,
  SignupShell,
  SignupTypeHint,
  SubmitRow,
  TermsBox,
  formatPhone,
  hasOptionalConsent,
  sanitizeName,
  validName,
} from "./signup-parts";

// 수요자 사전 초대 가입 (Figma ver.7 07 · 설계서 Front v0.5 slide 95~97).
// 초대 링크(?invite=)로 들어오면 ① 링크가 만료 · 사용 완료면 안내 화면, ② Entra 로그인 전에는 사전 등록 정보를 보여 주지 않고 로그인만 안내,
// ③ 로그인 계정이 명단과 일치할 때만 자동 입력된 가입 화면(이메일 수정 불가 · [인증] 없음 · 승인 없이 바로 가입),
// ④ 일치하지 않으면 일반 회원가입 화면(fallback)을 보여 줘요.

export function InviteExpiredScreen() {
  return (
    <NoticeScreen
      tone="neutral"
      title="만료되었거나 이미 사용된 초대 링크입니다"
      description="이미 회원가입을 마친 계정이라면 로그인해 주세요. 초대 링크 재발송이 필요하면 담당자에게 문의해 주세요."
      descriptionClassName="max-w-[319px]"
    />
  );
}

// 개발 서버 전용: 실제 Entra 없이 초대 명단 이메일로 로그인한 것처럼 신원 토큰을 만들어요 (백엔드 DEV_ENTRA_IDENTITY_ENABLED 필요)
function DevIdentityBox({ onIdentity }: { onIdentity: (token: string) => void }) {
  const [email, setEmail] = useState("");
  if (!import.meta.env.DEV) return null;
  return (
    <div className="mt-2 flex flex-col gap-2 rounded-nv-card border border-dashed border-nv-border-strong p-3">
      <p className="text-[12px] leading-[14px] text-nv-text-secondary">개발용 · 초대 명단 이메일로 Entra 로그인 흉내</p>
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="초대받은 이메일"
          className="h-10 flex-1 rounded-nv-control border border-nv-border-strong px-3 text-[13px] focus:outline-none"
        />
        <NvButton type="button" variant="secondary" disabled={!email.trim()} onClick={() => onIdentity(`dev-entra:${email.trim().toLowerCase()}`)}>
          확인
        </NvButton>
      </div>
    </div>
  );
}

function InviteLoginPrompt({ onLogin, onIdentity, error }: { onLogin: () => void; onIdentity: (token: string) => void; error: string | null }) {
  return (
    <NoticeScreen
      tone="brand"
      title="초대받은 계정으로 로그인해 주세요"
      description={
        <>
          초대 메일을 받은 Outlook 계정으로 로그인하면
          <br />
          사전 등록된 정보가 자동으로 입력돼요.
        </>
      }
    >
      <div className="flex flex-col items-center gap-3">
        <NvButton type="button" onClick={onLogin}>
          Outlook 계정으로 로그인
        </NvButton>
        {error && <p className="text-[12px] leading-[14px] text-nv-danger">{error}</p>}
        <DevIdentityBox onIdentity={onIdentity} />
      </div>
    </NoticeScreen>
  );
}

interface InviteFormState {
  company_name: string;
  ceo_name: string;
  company_country: string;
  requester_name: string;
  phone: string;
}

function InviteForm({ token, identity, lookup, onIdentityExpired }: { token: string; identity: string; lookup: InviteLookup; onIdentityExpired: () => void }) {
  const navigate = useNavigate();
  const prefill = lookup.prefill!;
  const [form, setForm] = useState<InviteFormState>({
    company_name: prefill.company_name,
    ceo_name: "",
    company_country: DEFAULT_COUNTRY,
    requester_name: prefill.requester_name,
    phone: formatPhone(prefill.phone),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [consents, setConsents] = useState<Record<string, boolean>>({});
  const [termsTab, setTermsTab] = useState<string | null>(null);
  const [popup, setPopup] = useState<string | null>(null);
  const [optionalPromptOpen, setOptionalPromptOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (key: keyof InviteFormState) => (value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: "" } : e));
  };

  const fieldErrors = (): Record<string, string> => {
    const out: Record<string, string> = {};
    if (!form.company_name.trim()) out.company_name = "필수 입력 항목입니다.";
    if (!form.ceo_name.trim()) out.ceo_name = "필수 입력 항목입니다.";
    if (!validName(form.requester_name)) out.requester_name = "한글 5자 · 영문 20자 이내로 입력해 주세요.";
    if (form.phone.replace(/\D/g, "").length < 9) out.phone = "전화번호를 확인해 주세요.";
    return out;
  };
  const allAgreed = REQUIRED_SIGNUP_TERMS.every((code) => consents[code]);
  const canSubmit = !submitting && allAgreed && Object.keys(fieldErrors()).length === 0;

  // 수요자가 선택 동의(제3자 제공) 없이 신청하면 "선택 동의 미동의 안내" 팝업을 한 번 거쳐요 (ver.7)
  const onSubmit = () => {
    const found = fieldErrors();
    setErrors(found);
    if (!canSubmit) return;
    if (!hasOptionalConsent(consents)) setOptionalPromptOpen(true);
    else void submit(consents);
  };

  // checked: 팝업 [동의하고 가입하기] 직후처럼 화면 상태가 아직 반영되기 전의 동의 값
  const submit = async (checked: Record<string, boolean>) => {
    setSubmitting(true);
    try {
      const tokens = await completeInviteSignup({
        token,
        entra_access_token: identity,
        company_name: form.company_name.trim(),
        ceo_name: form.ceo_name.trim(),
        company_country: form.company_country,
        requester_name: form.requester_name.trim(),
        phone: form.phone.replace(/\D/g, ""),
        consents: SIGNUP_TERMS.filter((code) => checked[code]),
      });
      storeTokens(tokens.access_token, tokens.refresh_token);
      clearInviteIdentity();
      navigate("/signup/invite-complete", { replace: true });
    } catch (error) {
      const apiError = getApiError(error);
      if (apiError?.code === "INVITE_EXPIRED") navigate(0);
      else if (getApiStatus(error) === 401) onIdentityExpired();
      else {
        setErrors((e) => ({ ...e, ...getApiFieldErrors(error) }));
        setPopup(apiError?.message ?? "가입을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SignupShell onSubmit={onSubmit}>
      <SignupHeading title="회원가입" description="초대받은 명단 정보가 자동 입력되었어요. 확인 후 가입 신청하면 바로 이용할 수 있어요." />

      <div className="flex w-fit rounded-nv-chip bg-nv-bg-page p-[3px]">
        <span className="w-20 rounded-nv-chip bg-nv-text-primary py-2 text-center text-[13px] font-bold leading-4 text-nv-text-inverse">수요자</span>
      </div>
      <SignupTypeHint />

      <SectionTitle>회사 정보</SectionTitle>
      <NvFormField inputClassName="rounded-nv-card" label={<NvRequiredLabel>회사명</NvRequiredLabel>} aria-required maxLength={100} value={form.company_name} onChange={(e) => set("company_name")(e.target.value)} error={errors.company_name} />
      <div className="grid gap-3 md:grid-cols-2">
        <NvFormField label={<NvRequiredLabel>대표명</NvRequiredLabel>} aria-required placeholder="대표명을 입력해 주세요" maxLength={100} value={form.ceo_name} onChange={(e) => set("ceo_name")(e.target.value)} error={errors.ceo_name} />
        <NvDropdownField label={<NvRequiredLabel>기업 소재국</NvRequiredLabel>} options={COUNTRY_OPTIONS} value={form.company_country} onChange={set("company_country")} />
      </div>

      <SectionTitle>계정 정보</SectionTitle>
      <div className="grid gap-3 md:grid-cols-2">
        <NvFormField label={<NvRequiredLabel>이름</NvRequiredLabel>} aria-required placeholder="한글 5자 · 영문 20자 이내" value={form.requester_name} onChange={(e) => set("requester_name")(sanitizeName(e.target.value))} error={errors.requester_name} />
        <NvFormField label={<NvRequiredLabel>전화번호</NvRequiredLabel>} aria-required inputMode="tel" value={form.phone} onChange={(e) => set("phone")(formatPhone(e.target.value))} error={errors.phone} />
      </div>
      <NvFormField inputClassName="rounded-nv-card read-only:bg-[#F5F7F7] read-only:text-[#6A6F77]" label={<NvRequiredLabel>이메일</NvRequiredLabel>} aria-required type="email" readOnly value={prefill.email} />

      <TermsBox consents={consents} onChange={setConsents} onView={setTermsTab} />
      <SubmitRow label="가입 신청" disabled={!canSubmit} />
      <LoginHint />

      <NvConsentPromptDialog
        open={optionalPromptOpen}
        onOpenChange={setOptionalPromptOpen}
        title={OPTIONAL_PROMPT.title}
        message={OPTIONAL_PROMPT.message}
        itemLabel={OPTIONAL_PROMPT.itemLabel}
        declineLabel={OPTIONAL_PROMPT.declineLabel}
        agreeLabel={OPTIONAL_PROMPT.agreeLabel}
        onView={() => setTermsTab(OPTIONAL_TERMS_CODE)}
        onDecline={() => {
          setOptionalPromptOpen(false);
          void submit(consents);
        }}
        onAgree={() => {
          const next = { ...consents, [OPTIONAL_TERMS_CODE]: true };
          setConsents(next);
          setOptionalPromptOpen(false);
          void submit(next);
        }}
        agreeing={submitting}
      />
      <NvTermsModal codes={[...SIGNUP_TERMS]} openCode={termsTab} onOpenCodeChange={setTermsTab} labels={SIGNUP_TERMS_TAB_LABELS} />
      <NvAlertDialog open={popup !== null} onOpenChange={(open) => !open && setPopup(null)} message={popup} actions={<NvButton onClick={() => setPopup(null)}>확인</NvButton>} />
    </SignupShell>
  );
}

export function SignupInvitePage({ token, fallback }: { token: string; fallback: ReactNode }) {
  const navigate = useNavigate();
  const [identity, setIdentity] = useState<string | null>(() => readInviteIdentity());
  const [lookup, setLookup] = useState<InviteLookup | null>(null);
  const [failed, setFailed] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Entra 로그인(신원 확인만) 결과가 sessionStorage에 저장되면 읽어 와요
  const onSuccess = useCallback(() => setIdentity(readInviteIdentity()), []);
  const onError = useCallback((error: unknown) => setLoginError(error instanceof Error ? error.message : "로그인하지 못했습니다. 다시 시도해 주세요."), []);
  const { isCallback, processing, startLogin } = useSsoLogin({ onSuccess, onError });

  useEffect(() => {
    let cancelled = false;
    setLookup(null);
    setFailed(false);
    lookupInvite(token, identity)
      .then((result) => !cancelled && setLookup(result))
      .catch((error) => {
        if (cancelled) return;
        // 신원 토큰이 거절되면 다시 로그인하도록 비우고, 그 외에는 다시 시도 안내
        if (identity && getApiStatus(error) === 401) {
          clearInviteIdentity();
          setIdentity(null);
        } else setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token, identity]);

  const acceptIdentity = (value: string) => {
    storeInviteIdentity(value);
    setLoginError(null);
    setIdentity(value);
  };

  if (isCallback) return processing ? <SpinnerOverlay /> : null;
  if (failed) {
    return (
      <NoticeScreen tone="neutral" title="초대 링크를 확인하지 못했습니다" description="잠시 후 다시 시도해 주세요.">
        <NvButton type="button" onClick={() => navigate(0)}>
          다시 시도
        </NvButton>
      </NoticeScreen>
    );
  }
  if (!lookup) return <SpinnerOverlay />;
  if (lookup.state === "expired") return <InviteExpiredScreen />;
  if (!identity) {
    return (
      <>
        {processing && <SpinnerOverlay />}
        <InviteLoginPrompt
          error={loginError}
          onIdentity={acceptIdentity}
          onLogin={() => {
            setLoginError(null);
            void startLogin("outlook", `${NV_PATHS.signup}?invite=${encodeURIComponent(token)}`, { identityOnly: true });
          }}
        />
      </>
    );
  }
  // 로그인 계정이 명단과 다르면 자동 입력 없이 일반 회원가입 화면 (승인 대기 흐름)
  if (!lookup.matched || !lookup.prefill) return <>{fallback}</>;
  return (
    <InviteForm
      token={token}
      identity={identity}
      lookup={lookup}
      onIdentityExpired={() => {
        clearInviteIdentity();
        setIdentity(null);
      }}
    />
  );
}
